import { Spinner } from "@cafe/ui";
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { deviceEntry, deviceHome, getDevice } from "@/features/terminal/device";
import { refreshSession, restoreImpersonation, useAuth } from "./store";
import { BrandTheme } from "@/shared/ui/brand";

let started = false;
/** При загрузке приложения пробуем восстановить сессию по HttpOnly refresh-cookie. */
export function useBootstrapSession() {
  useEffect(() => {
    if (started) return;
    started = true;
    // Сеанс поддержки платформы задаёт сессию сам (без refresh-cookie); переживает перезагрузку вкладки до истечения срока
    if (window.location.pathname === "/impersonate") return;
    if (restoreImpersonation()) return;
    void refreshSession().then((ok) => !ok && !useAuth.getState().impersonation && useAuth.getState().clear());
  }, []);
}

function FullScreenLoader() {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg" aria-busy>
      <Spinner className="size-8 text-primary" />
    </div>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const screen = useAuth((s) => !!s.screen);
  const location = useLocation();
  if (status === "unknown") return <FullScreenLoader />;
  // Экран без входа не открывает разделы ERP — только свой экран
  if (screen) return <Navigate to="/screen" replace />;
  if (status === "anonymous") {
    const entry = deviceEntry();
    if (entry) return <Navigate to={entry} replace />;
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <><BrandTheme />{children}</>;
}

export function GuestOnly({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  // Экран без входа — «гость»: с него менеджер входит по PIN
  const screen = useAuth((s) => !!s.screen);
  if (status === "unknown") return <FullScreenLoader />;
  // Вход по PIN на устройстве — сразу его рабочий экран (касса, KDS, доставки)
  if (status === "authenticated" && !screen) {
    const device = getDevice();
    return <Navigate to={device ? deviceHome(device.type) : "/"} replace />;
  }
  return <>{children}</>;
}
